import { FormEvent, ReactNode } from 'react';
import ButtonSpinner from '../../assets/buttonLoading/buttonLoading';
import LoginButton from '../../component/button/button';
import './login.css';
import ShowEye from '../../assets/show_eye.png';
import CloseEye from '../../assets/close_eye.png';
import {
  LoginToggleKey,
  LoginTranslation,
  StaffPinLength,
} from '../../utils/helper.type';
import LoginToggleMenu from '../../component/loginToggleMenu/loginToggleMenu';
import { Trans } from 'react-i18next';
import { LanguageToggle } from '../../component/languageToggle/languageToggle';
import DesktopPinInput from '../../component/desktopPinInput/desktopPinInput';
import TabletPinInput from '../../component/tabletPinInput/tabletPinInput';
import TabletLayoutImage from '../../component/tabletLayoutImage/tabletLayoutImage';
import DesktopLayoutImage from '../../component/desktopLayoutmage/desktopLayoutImage';
import RenderInPlayLogo from '../../component/inPlayLogo/inPlayLogo';
import { getIdAttributes } from '../../utils/helper';
import { IdPrefix } from '../../utils/constant';

export const idPrefix = 'login';
/**
 * A reusable component that renders an email input field. It conditionally displays a label
 * based on the `isDesktop` prop. The label will be shown only if the `isDesktop` flag is true.
 * The input value is bound to the `email` state, and changes are handled by the `setEmail` function.
 *
 * @param {ReactNode} [label] - The label to display for the input field. This is only shown on desktop view.
 * @param {string} [email] - The current value of the email input field.
 * @param {(email: string) => void} [setEmail] - The setter function to update the email state.
 * @param {string} [placeholder] - The placeholder text for the email input field.
 * @param {boolean} [isDesktop] - A flag indicating if the view is desktop. If true, the label is displayed.
 *
 * @returns {JSX.Element} The email input field, optionally including a label depending on `isDesktop`.
 */
export const renderEmailInput = (
  label?: ReactNode,
  email?: string,
  setEmail?: (email: string) => void,
  placeholder?: string,
  isDesktop?: boolean,
  hasError?: boolean,
): JSX.Element => {
  return (
    <div data-testid="email-input-wrapper">
      {!isDesktop && (
        <label
          data-testid="email-input-label"
          htmlFor="email"
          className="login-input-label"
        >
          {label}
        </label>
      )}
      <input
        type="email"
        id="email"
        placeholder={placeholder}
        className={`login-input font-helvetica ${
          hasError ? 'border-2 border-[#D80000] focus:ring-[#D80000]' : ''
        }`}
        value={email}
        onChange={(e) => setEmail?.(e.target.value)}
        data-testid="email-input-field"
      />
    </div>
  );
};
/**
 * Renders a password input field with a label, visibility toggle, and placeholder.
 *
 * This component allows the user to enter a password and toggle the visibility of the entered password.
 * It consists of:
 * - A label for the password input field.
 * - An input field for entering the password, with its value controlled by the `password` state.
 * - A button (eye icon) to toggle the visibility of the entered password.
 *
 * @param label - The label to be displayed for the input field.
 * @param password - The current value of the password entered by the user.
 * @param setPassword - A function to update the `password` state when the input changes.
 * @param placeholder - The placeholder text to show when the input field is empty.
 * @param showPassword - A boolean indicating whether the password is visible or hidden.
 * @param setShowPassword - A function to update the `showPassword` state when the visibility toggle is clicked.
 *
 * @returns A JSX element representing the password input with the toggleable visibility.
 */

export const renderPasswordInput = (
  label?: ReactNode,
  password?: string,
  setPassword?: (password: string) => void,
  placeholder?: string,
  showPassword?: boolean,
  setShowPassword?: (show: boolean) => void,
  hasError?: boolean,
): JSX.Element => (
  <div
    className="relative"
    {...getIdAttributes(idPrefix, 'password-input-wrapper')}
  >
    <label
      htmlFor="password"
      className="login-input-label login-font-style mt-4"
      {...getIdAttributes(idPrefix, 'password-input-label')}
    >
      {label}
    </label>
    <input
      type={showPassword ? 'text' : 'password'}
      id="password"
      placeholder={placeholder}
      className={`login-input font-helvetica ${
        hasError ? 'border-2 border-[#D80000] focus:ring-[#D80000]' : ''
      }`}
      value={password}
      onChange={(e) => setPassword?.(e.target.value)}
      data-testid="login-password-input-field"
    />
    <div
      className="passowrd-input-container absolute right-4 top-[52px] cursor-pointer"
      onClick={() => setShowPassword?.(!showPassword)}
      data-testid="password-toggle-btn"
    >
      {showPassword ? (
        <img src={ShowEye} alt="Show Password" className="w-5 h-4" />
      ) : (
        <img src={CloseEye} alt="Hide Password" className="w-5 h-3" />
      )}
    </div>
  </div>
);

/**
 * Renders a styled password input field with a label, optional error state, and show/hide functionality.
 *
 * This function is intended for use in forms such as "Change Password", where the user needs to input or confirm a password.
 * It aligns the label and input in a single horizontal row using Flexbox.
 *
 * @param {ReactNode} label - The text or element to be displayed as the label for the input.
 * @param {string} password - The current value of the password input field.
 * @param {(password: string) => void} setPassword - Function to update the password value.
 * @param {string} placeholder - Placeholder text for the input field.
 * @param {boolean} showPassword - Whether the password should be visible as plain text.
 * @param {(show: boolean) => void} setShowPassword - Function to toggle password visibility.
 * @param {boolean} hasError - Whether the input has a validation error (adds red border).
 *
 * @returns {JSX.Element} A JSX element containing the label, input field, and toggle icon.
 */
export const renderChangePasswordInput = (
  label?: ReactNode,
  password?: string,
  setPassword?: (password: string) => void,
  placeholder?: string,
  showPassword?: boolean,
  setShowPassword?: (show: boolean) => void,
  hasError?: boolean,
): JSX.Element => (
  <div
    className="flex items-center gap-4 mt-4"
    data-testid="change-password-wrapper"
  >
    <label
      data-testid="change-password-label"
      htmlFor="password"
      className="w-40 text-sm  whitespace-nowrap text-[#1A1A1A]"
    >
      {label}
    </label>

    <div className="relative flex-1">
      <input
        type={showPassword ? 'text' : 'password'}
        id="password"
        placeholder={placeholder}
        className={`w-full px-4 py-2 rounded-full bg-INPUT_THEME_COLOR focus:outline-none focus:ring-2 focus:ring-THEME_COLOR pr-10 placeholder:text-sm ${
          hasError ? 'border-2 border-[#D80000]' : ''
        }`}
        value={password}
        onChange={(e) => setPassword?.(e.target.value)}
        data-testid="change-password-input"
      />

      <div
        className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
        onClick={() => setShowPassword?.(!showPassword)}
        data-testid="change-password-toggle-btn"
      >
        {showPassword ? (
          <img src={ShowEye} alt="Show Password" className="w-5 h-4" />
        ) : (
          <img src={CloseEye} alt="Hide Password" className="w-5 h-3" />
        )}
      </div>
    </div>
  </div>
);

/**
 * Renders the submit button for the login form.
 *
 * @param {boolean} isDisabled - Determines whether the button should be disabled.
 * @param {boolean} isLoading - Indicates whether a loading spinner should be displayed.
 * @returns {JSX.Element} A JSX element containing the login button.
 */
export const renderSubmitButton = (
  isDisabled: boolean,
  isLoading: boolean,
  label: ReactNode,
): JSX.Element => (
  <div
    className="flex items-center justify-center mt-12"
    data-testid="submit-button-wrapper"
  >
    <LoginButton
      {...getIdAttributes(IdPrefix.BUTTON, 'submit')}
      className="login-button"
      disabled={isDisabled}
    >
      {isLoading ? <ButtonSpinner data-testid="button-spinner" /> : label}
    </LoginButton>
  </div>
);

/**
 * Renders the desktop view of the PIN input form, including:
 * - A multi-digit PIN input field
 * - A "Forgot PIN" link
 * - A submit button
 *
 * @param otp - An array representing the current state of each digit in the PIN input
 * @param setOtp - Setter function to update the OTP state
 * @param isDisabled - Flag to disable the submit button
 * @param isLoading - Flag to show a loading indicator on the submit button
 * @param onForgotClick - Callback for when the "Forgot PIN" link is clicked
 *
 * @returns A JSX element representing the PIN input section for desktop screens
 */
export const renderDesktopPinInput = (
  otp: string,
  setOtp: React.Dispatch<React.SetStateAction<string>>,
  isDisabled: boolean,
  isLoading: boolean,
  handleForgotPinClick: () => void,
  handleStaffLogin: (event: FormEvent<HTMLFormElement>) => void,
  loginAsStaffError: string | null,
) => {
  return (
    <form
      className="desktop-pin-input"
      onSubmit={handleStaffLogin}
      data-testid="desktop-pin-form"
    >
      <DesktopPinInput
        data-testid="desktop-pin-input-field"
        otp={otp}
        setOtp={setOtp}
        placeholder="0"
        inputLength={StaffPinLength}
        hasError={!!loginAsStaffError}
      />
      {loginAsStaffError && (
        <p data-testid="pin-error" className="text-base text-[#D80000] mt-2">
          {loginAsStaffError}
        </p>
      )}
      <div className="mt-14 mb-28">
        <span
          data-testid="forgot-pin-link"
          onClick={handleForgotPinClick}
          className="text-sm text-black underline cursor-pointer"
        >
          <Trans i18nKey="login.forgotPin" />
        </span>
      </div>
      <div data-testid="submit-button-container">
        {renderSubmitButton(
          isDisabled ?? false,
          isLoading ?? false,
          <Trans i18nKey="login.button" />,
        )}
      </div>
    </form>
  );
};

/**
 * Renders the PIN input UI for tablet devices using the `TabletPinInput` component.
 *
 * This function conditionally renders the component only if both `pin` and `setPin` are defined.
 * It provides a styled container to enter the OTP (PIN) with customizable length and label.
 *
 * @param otpLength - Number of digits expected in the PIN
 * @param authText - Instructional or label text displayed above the PIN input
 * @param pin - The current PIN value
 * @param setPin - Setter function to update the PIN state
 *
 * @returns A JSX element rendering the tablet PIN input layout, or null if `pin` or `setPin` is not provided
 */
export const renderTabletPinInput = (
  otpLength: number,
  authText: string,
  handleAuthClick: () => void,
  pin?: string,
  setPin?: React.Dispatch<React.SetStateAction<string>>,
  handleVerifyOtp?: (event: FormEvent) => void,
  loginAsStaffLoading?: boolean,
  loginAsStaffError?: string | null,
) => {
  if (pin === undefined || setPin === undefined) {
    return null;
  }

  return (
    <div className="otp-input-container" data-testid="tablet-pin-form">
      <div className="tablet-otp-container">
        <div className="bg-white rounded-2xl p-2 md:p-0 w-full">
          <TabletPinInput
            data-testid="tablet-pin-input"
            pin={pin}
            setPin={setPin}
            otpLength={otpLength}
            loginAsStaffLoading={loginAsStaffLoading}
            authText={authText}
            handleAuthClick={handleAuthClick}
            handleVerifyOtp={handleVerifyOtp}
            isVerifyLoading={loginAsStaffLoading}
            error={loginAsStaffError}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Renders the email and password input form for both Admin and Staff login views.
 *
 * Includes:
 * - Email input field
 * - Password input field with optional show/hide toggle
 * - "Forgot Password" link (conditionally displayed for Admin or Staff based on `activeToggleKey`)
 * - Submit button
 *
 * @param activeToggleKey - Indicates the current login type (Admin or Staff)
 * @param email - The email value entered by the user
 * @param setEmail - Setter function to update the email state
 * @param password - The password value entered by the user
 * @param setPassword - Setter function to update the password state
 * @param loginTranslation - Translations for input placeholders or validation messages
 * @param showPassword - Controls visibility of the password text
 * @param setShowPassword - Setter function to toggle password visibility
 * @param isDisabled - Disables the submit button if true
 * @param isLoading - Shows a loading indicator on the submit button if true
 * @param handleSubmit - Form submission handler
 * @param setIsForgotModalOpen - Function to open the "Forgot Password" modal
 * @param isDesktop - Flag to check if the view is on a desktop device
 *
 * @returns A JSX form element containing login inputs and actions
 */
export const renderEmailAndPasswordInputs = (
  onForgotClick: () => void,
  error?: string | null,
  email?: string,
  setEmail?: React.Dispatch<React.SetStateAction<string>>,
  password?: string,
  setPassword?: React.Dispatch<React.SetStateAction<string>>,
  loginTranslation?: LoginTranslation,
  showPassword?: boolean,
  setShowPassword?: React.Dispatch<React.SetStateAction<boolean>>,
  isDisabled?: boolean,
  isLoading?: boolean,
  handleSubmit?: (e: React.FormEvent) => void,
) => {
  return (
    <form data-testid="login-form" onSubmit={handleSubmit}>
      {renderEmailInput(
        <Trans i18nKey="login.email" />,
        email,
        setEmail,
        loginTranslation?.email ?? '',
        false,
        !!error,
      )}

      {renderPasswordInput(
        <Trans i18nKey="login.password" />,
        password ?? '',
        setPassword ?? (() => {}),
        loginTranslation?.password ?? '',
        showPassword ?? false,
        setShowPassword ?? (() => {}),
        !!error,
      )}

      {error && (
        <p data-testid="login-error" className="text-base text-[#D80000] mt-2">
          {error}
        </p>
      )}

      <div className="text-right mt-10">
        <span
          data-testid="forgot-password"
          onClick={onForgotClick}
          className="text-base text-black underline cursor-pointer"
        >
          <Trans i18nKey="forgotPassword.forgotText" />
        </span>
      </div>

      <div data-testid="submit-button-wrapper">
        {renderSubmitButton(
          isDisabled ?? false,
          isLoading ?? false,
          <Trans i18nKey="login.button" />,
        )}
      </div>
    </form>
  );
};

/**
 * Renders the desktop view for the admin login screen.
 *
 * This function is responsible for rendering the login page on large screens (desktops).
 * It includes a login form with fields for email and password, a toggle menu,
 * a language selection dropdown, and an image layout. Additionally, it handles form submission,
 * renders the forgot password link, and displays the submit button.
 *
 * @param activeLoginKey - The key representing the current active login state (e.g., login or sign-up).
 * @param handleToggle - A function that toggles between the different login states (e.g., login and sign-up).
 * @param email - The email input value for the login form.
 * @param setEmail - The setter function to update the email input value.
 * @param password - The password input value for the login form.
 * @param setPassword - The setter function to update the password input value.
 * @param loginTranslation - The translations for various text elements in the login screen.
 * @param isDisabled - A boolean indicating whether the submit button should be disabled.
 * @param isLoading - A boolean indicating whether the login form is in a loading state (e.g., while submitting).
 * @param handleSubmit - The function to handle the form submission.
 * @param showPassword - A boolean flag to toggle the visibility of the password input field.
 * @param setShowPassword - The setter function to toggle the visibility of the password input.
 * @param selectedLanguage - The current selected language for the application.
 * @param setSelectedLanguage - The setter function to update the selected language.
 *
 * @returns JSX Element - The rendered desktop login view.
 */

export const renderDesktopView = (
  activeLoginKey: LoginToggleKey,
  handleToggle: (option: LoginToggleKey) => void,
  email: string,
  setEmail: React.Dispatch<React.SetStateAction<string>>,
  password: string,
  setPassword: React.Dispatch<React.SetStateAction<string>>,
  loginTranslation: LoginTranslation,
  isPinLoginButtonDisabled: boolean,
  isEmailLoginButtonDisabled: boolean,
  isLoading: boolean,
  handleSubmit: (e: React.FormEvent) => void,
  showPassword: boolean,
  setShowPassword: React.Dispatch<React.SetStateAction<boolean>>,
  selectedLanguage: string,
  setSelectedLanguage: React.Dispatch<React.SetStateAction<string>>,
  onForgotClick: () => void,
  loginPin: string,
  setLoginPin: React.Dispatch<React.SetStateAction<string>>,
  handleForgotPinClick: () => void,
  isDisplayPIN: boolean | null,
  error: string | null,
  handleViewTermModal: () => void,
  handleViewPrivacyModal: () => void,
  handleStaffLogin: (event: FormEvent) => void,
  loginAsStaffLoading: boolean,
  loginAsStaffError: string | null,
) => {
  return (
    <div className="lg:flex h-screen w-full" data-testid="desktop-view">
      <div className="login-container" data-testid="login-container">
        <div
          className="login-form-container"
          data-testid="login-form-container"
        >
          <div className="mt-24 justify-center">
            <LoginToggleMenu
              active={activeLoginKey}
              onToggle={handleToggle}
              data-testid="login-toggle-menu"
            />
          </div>
          <div
            className="login-header-container mb-[70px] mx-auto custom:ml-20"
            data-testid="login-header-container"
          >
            <div>
              <RenderInPlayLogo data-testid="login-logo" />
              <h1 className="login-header" data-testid="login-title">
                <Trans i18nKey="login.title" />
              </h1>
              {isDisplayPIN === true &&
                activeLoginKey === LoginToggleKey.STAFF && (
                  <p
                    className="mt-2 text-base text-[#1A1A1A]"
                    data-testid="login-desc"
                  >
                    <Trans i18nKey="login.loginDesc" />
                  </p>
                )}
            </div>
          </div>
          <div
            className="w-full max-w-md xl:max-w-xl mx-auto custom:ml-20"
            data-testid="login-form-wrapper"
          >
            {isDisplayPIN === true && activeLoginKey === LoginToggleKey.STAFF
              ? renderDesktopPinInput(
                  loginPin,
                  setLoginPin,
                  isPinLoginButtonDisabled,
                  loginAsStaffLoading,
                  handleForgotPinClick,
                  handleStaffLogin,
                  loginAsStaffError,
                )
              : renderEmailAndPasswordInputs(
                  onForgotClick,
                  error,
                  email,
                  setEmail,
                  password,
                  setPassword,
                  loginTranslation,
                  showPassword,
                  setShowPassword,
                  isEmailLoginButtonDisabled,
                  isLoading,
                  handleSubmit,
                )}
          </div>
          <p
            className="text-sm text-black mt-20"
            data-testid="login-terms-text"
          >
            <Trans i18nKey="login.agree" />,{' '}
            <span
              onClick={handleViewTermModal}
              className="terms-privacy-text cursor-pointer underline"
              data-testid="terms-link"
            >
              <Trans i18nKey="login.terms" />
            </span>{' '}
            <Trans i18nKey="login.and" />{' '}
            <span
              onClick={handleViewPrivacyModal}
              className="terms-privacy-text cursor-pointer underline"
              data-testid="privacy-link"
            >
              <Trans i18nKey="login.policy" />
            </span>
            .
          </p>
        </div>
      </div>

      <LanguageToggle
        selected={selectedLanguage}
        setSelected={setSelectedLanguage}
        data-testid="language-toggle"
      />
      <DesktopLayoutImage data-testid="desktop-layout-image" />
    </div>
  );
};

/**
 * Renders the tablet view layout for the login screen.
 * Depending on the selected login toggle key, it either renders:
 * - Email and password login form (for admins)
 * - OTP pin input form (for other users)
 *
 * Includes background image, layout styles, and form elements specific to tablet screens.
 *
 * @param activeLoginKey - Currently active login option (e.g., ADMIN, USER).
 * @param handleToggle - Function to switch between login types.
 * @param activeToggleKey - Key used to conditionally render the correct login form.
 * @param otpLength - Expected length of OTP/pin input.
 * @param authText - Informational/authentication text to display above pin input.
 * @param email - Controlled value for the email input field.
 * @param setEmail - Setter function for email state.
 * @param password - Controlled value for the password input field.
 * @param setPassword - Setter function for password state.
 * @param loginTranslation - Translated labels and messages used in login form.
 * @param isDisabled - Whether the login button is disabled.
 * @param isLoading - Whether the login action is currently loading.
 * @param handleSubmit - Form submit handler.
 * @param showPassword - Whether the password is visible or masked.
 * @param setShowPassword - Setter function to toggle password visibility.
 * @param isDesktop - Boolean flag to determine device type for conditional rendering.
 * @param setIsForgotModalOpen - Function to open the "forgot password" modal.
 * @param pin - Controlled value for the pin/OTP input.
 * @param setPin - Setter function for pin/OTP input.
 *
 * @returns JSX.Element - The complete tablet login layout.
 */
export const renderTabletView = (
  handleViewTermModal: () => void,
  handleViewPrivacyModal: () => void,
  activeLoginKey: LoginToggleKey,
  handleToggle: (option: LoginToggleKey) => void,
  isDisplayPIN: boolean | null,
  otpLength: number,
  authText: string,
  onForgotClick: () => void,
  handleAuthClick: () => void,
  email?: string,
  setEmail?: React.Dispatch<React.SetStateAction<string>>,
  password?: string,
  setPassword?: React.Dispatch<React.SetStateAction<string>>,
  loginTranslation?: LoginTranslation,
  isDisabled?: boolean,
  isLoading?: boolean,
  handleSubmit?: (e: React.FormEvent) => void,
  showPassword?: boolean,
  setShowPassword?: React.Dispatch<React.SetStateAction<boolean>>,
  pin?: string,
  setPin?: React.Dispatch<React.SetStateAction<string>>,
  error?: string | null,
  handleStaffLogin?: (event: FormEvent) => void,
  loginAsStaffLoading?: boolean,
  loginAsStaffError?: string | null,
) => {
  return (
    <div className="relative min-h-screen w-full" data-testid="tablet-view">
      <TabletLayoutImage data-testid="tablet-layout-image" />
      <div className="absolute inset-0 bg-black/30" data-testid="overlay" />

      <div
        className="tablet-login-container"
        data-testid="tablet-login-container"
      >
        <div className="flex flex-col">
          <div className="mt-10 md:mt-5" data-testid="login-toggle-wrapper">
            <LoginToggleMenu
              active={activeLoginKey}
              onToggle={handleToggle}
              data-testid="login-toggle-menu"
            />
          </div>

          <div
            className="text-white mt-auto mb-10 md:mb-15"
            data-testid="tablet-login-header"
          >
            <RenderInPlayLogo className={'mb-10 h-10'} />
            <h1
              className="text-6xl sm:text-5xl login-font-style mb-14"
              data-testid="tablet-login-title"
            >
              <Trans i18nKey="login.title" />
            </h1>
            <p
              className="text-base max-w-xs"
              data-testid="tablet-login-agreement"
            >
              <Trans i18nKey="login.tabletAgree" />{' '}
              <span
                className={'cursor-pointer'}
                onClick={handleViewTermModal}
                data-testid="terms-link"
              >
                <Trans i18nKey="login.terms" />
              </span>{' '}
              &{' '}
              <span
                className={'cursor-pointer'}
                onClick={handleViewPrivacyModal}
                data-testid="privacy-link"
              >
                <Trans i18nKey="login.policy" />
              </span>
            </p>
          </div>
        </div>

        <div
          className="flex flex-col justify-center items-center h-full"
          data-testid="tablet-login-form-wrapper"
        >
          {isDisplayPIN === true && activeLoginKey === LoginToggleKey.STAFF ? (
            renderTabletPinInput(
              otpLength,
              authText,
              handleAuthClick,
              pin,
              setPin,
              handleStaffLogin,
              loginAsStaffLoading,
              loginAsStaffError,
            )
          ) : (
            <div className="tablet-login-form">
              {renderEmailAndPasswordInputs(
                onForgotClick,
                error,
                email,
                setEmail,
                password,
                setPassword,
                loginTranslation,
                showPassword,
                setShowPassword,
                isDisabled,
                isLoading,
                handleSubmit,
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
